import {
  DealActivityFragmentFragment,
  DealActivityType,
} from "../graphql/generated";

const FILE_VIEW_OR_DOWNLOAD_TYPES = [
  DealActivityType.FileViewed,
  DealActivityType.FileDownloaded,
];

const GROUPABLE_TYPES = new Set([
  DealActivityType.FileViewed,
  DealActivityType.FileDownloaded,
  DealActivityType.FileCreated,
  DealActivityType.FileDeleted,
  DealActivityType.FolderCreated,
  DealActivityType.FolderDeleted,
  DealActivityType.QuestionDocumentsFound,
  DealActivityType.QuestionAsked,
  DealActivityType.QuestionAnswered,
  DealActivityType.TaskCreated,
]);

// --- DDQL tree types & helpers ---

interface DdqlPathEntry {
  id: string;
  name: string;
}

export interface DdqlTreeNode {
  ddqlId: string;
  ddqlName: string;
  activities: DealActivityFragmentFragment[];
  children: DdqlTreeNode[];
}

/**
 * Returns the root (top-level) DDQL for a QuestionDocumentsFound activity.
 * parentDdqls is ordered [immediateParent, ..., root], so the last element is root.
 * If no parents, the question's own ddql is the root.
 */
function getTopLevelDdql(
  activity: DealActivityFragmentFragment,
): DdqlPathEntry | null {
  const ddql = activity.question?.ddql;
  if (!ddql) return null;

  const parents = ddql.parentDdqls;
  if (parents.length > 0) {
    return parents[parents.length - 1];
  }
  return { id: ddql.id, name: ddql.name };
}

/**
 * Returns the full DDQL path from root to the question's own DDQL.
 * parentDdqls = [immediateParent, ..., root], so we reverse and append the ddql itself.
 */
function getDdqlPath(activity: DealActivityFragmentFragment): DdqlPathEntry[] {
  const ddql = activity.question?.ddql;
  if (!ddql) return [];

  const parents = ddql.parentDdqls;
  const reversed = [...parents].reverse();
  reversed.push({ id: ddql.id, name: ddql.name });
  return reversed;
}

/**
 * Builds a tree of DdqlTreeNodes from a group of QuestionDocumentsFound activities.
 * All activities should share the same root DDQL.
 */
export function buildDdqlTree(
  activities: DealActivityFragmentFragment[],
): DdqlTreeNode | null {
  if (activities.length === 0) return null;

  const topLevel = getTopLevelDdql(activities[0]);
  if (!topLevel) return null;

  const root: DdqlTreeNode = {
    ddqlId: topLevel.id,
    ddqlName: topLevel.name,
    activities: [],
    children: [],
  };

  for (const activity of activities) {
    const path = getDdqlPath(activity);
    if (path.length === 0) {
      root.activities.push(activity);
      continue;
    }

    // Walk the path starting from depth 1 (skip root, which is path[0])
    let node = root;
    for (let depth = 1; depth < path.length; depth++) {
      const entry = path[depth];
      let child = node.children.find((c) => c.ddqlId === entry.id);
      if (!child) {
        child = {
          ddqlId: entry.id,
          ddqlName: entry.name,
          activities: [],
          children: [],
        };
        node.children.push(child);
      }
      node = child;
    }

    // Place the activity at the deepest matching DDQL node
    node.activities.push(activity);
  }

  return root;
}

/**
 * Counts all activities in a tree node and its descendants.
 */
export function countTreeActivities(node: DdqlTreeNode): number {
  let count = node.activities.length;
  for (const child of node.children) {
    count += countTreeActivities(child);
  }
  return count;
}

/**
 * Counts total files across all activities in a tree node and its descendants.
 */
export function countTreeFiles(node: DdqlTreeNode): number {
  let count = 0;
  for (const activity of node.activities) {
    count += activity.questionDocumentsFoundData?.files.length ?? 0;
  }
  for (const child of node.children) {
    count += countTreeFiles(child);
  }
  return count;
}

/**
 * Collects all activities from a tree node and its descendants into a flat list.
 */
export function flattenTreeActivities(
  node: DdqlTreeNode,
): DealActivityFragmentFragment[] {
  const result = [...node.activities];
  for (const child of node.children) {
    result.push(...flattenTreeActivities(child));
  }
  return result;
}

// --- Sequential grouping ---

export function getGroupKey(activity: DealActivityFragmentFragment): string {
  const { type, account } = activity;

  if (!GROUPABLE_TYPES.has(type)) {
    return `unique:${activity.id}`;
  }

  // QuestionDocumentsFound groups by root (top-level) DDQL
  if (type === DealActivityType.QuestionDocumentsFound) {
    if (activity.question && !activity.question.ddql) {
      return `question:${activity.question.id}:${type}`;
    }

    const topLevel = getTopLevelDdql(activity);
    const ddqlId = topLevel?.id ?? "unknown";
    return `ddql:${ddqlId}:${type}`;
  }

  // QuestionAsked/QuestionAnswered group by root (top-level) DDQL + account
  if (type === DealActivityType.QuestionAsked || type === DealActivityType.QuestionAnswered) {
    if (activity.question && !activity.question.ddql) {
      return `unique:${activity.id}`;
    }

    const topLevel = getTopLevelDdql(activity);
    const ddqlId = topLevel?.id ?? "unknown";
    const accountId = account?.id ?? "unknown";
    return `ddql:${ddqlId}:${type}:account:${accountId}`;
  }

  const accountId = account?.id ?? "unknown";

  if (FILE_VIEW_OR_DOWNLOAD_TYPES.includes(type)) {
    return `account:${accountId}:FILE_VIEW_OR_DOWNLOAD`;
  }

  if (type === DealActivityType.FileCreated) {
    const folderId = activity.file?.folderId ?? activity.folder?.id ?? "unknown";
    return `folder:${folderId}:${type}:account:${accountId}`;
  }

  return `account:${accountId}:${type}`;
}

export interface ActivityGroup {
  activities: DealActivityFragmentFragment[];
  groupKey: string;
}

/**
 * Groups activities sequentially. Only consecutive items with the same
 * group key are merged. E.g. User A x6, User B x2, User A x3 = 3 groups.
 */
export function groupActivitiesSequentially(
  activities: DealActivityFragmentFragment[],
): ActivityGroup[] {
  if (activities.length === 0) {
    return [];
  }

  const groups: ActivityGroup[] = [];
  let currentGroup: ActivityGroup = {
    activities: [activities[0]],
    groupKey: getGroupKey(activities[0]),
  };

  for (let i = 1; i < activities.length; i++) {
    const activity = activities[i];
    const key = getGroupKey(activity);

    if (key === currentGroup.groupKey) {
      currentGroup.activities.push(activity);
    } else {
      groups.push(currentGroup);
      currentGroup = {
        activities: [activity],
        groupKey: key,
      };
    }
  }

  groups.push(currentGroup);
  return groups;
}

function getGroupMaxTimestamp(group: ActivityGroup): number {
  return Math.max(...group.activities.map((a) => a.createdAt));
}

function getGroupMinTimestamp(group: ActivityGroup): number {
  return Math.min(...group.activities.map((a) => a.createdAt));
}

/**
 * Merges non-consecutive groups that share the same groupKey and fall
 * within a time window. After merging, re-sorts by most recent activity
 * timestamp (descending) so merged groups appear at the right position.
 */
function mergeGroupsWithinTimeWindow(
  groups: ActivityGroup[],
  windowMs: number,
): ActivityGroup[] {
  const windowSeconds = windowMs / 1000;
  const merged: ActivityGroup[] = [];

  for (const group of groups) {
    if (group.groupKey.startsWith("unique:")) {
      merged.push(group);
      continue;
    }

    const existingIndex = merged.findIndex(
      (g) =>
        g.groupKey === group.groupKey &&
        Math.abs(getGroupMaxTimestamp(g) - getGroupMinTimestamp(group)) <=
          windowSeconds,
    );

    if (existingIndex !== -1) {
      merged[existingIndex].activities.push(...group.activities);
    } else {
      merged.push({ ...group, activities: [...group.activities] });
    }
  }

  merged.sort((a, b) => getGroupMaxTimestamp(b) - getGroupMaxTimestamp(a));

  return merged;
}

/**
 * Groups activities: first sequentially by key, then merges same-key
 * groups within a time window.
 */
export function groupActivities(
  activities: DealActivityFragmentFragment[],
  windowMs: number = 15 * 60 * 1000,
): ActivityGroup[] {
  const sequential = groupActivitiesSequentially(activities);
  return mergeGroupsWithinTimeWindow(sequential, windowMs);
}

export function isFileViewOrDownloadGroup(group: ActivityGroup): boolean {
  return group.groupKey.endsWith(":FILE_VIEW_OR_DOWNLOAD");
}

export function isQuestionDocumentsFoundGroup(group: ActivityGroup): boolean {
  return group.groupKey.endsWith(`:${DealActivityType.QuestionDocumentsFound}`);
}

export function isFileCreatedGroup(group: ActivityGroup): boolean {
  return group.groupKey.includes(`:${DealActivityType.FileCreated}:`);
}

export function isQuestionAskedGroup(group: ActivityGroup): boolean {
  return group.groupKey.includes(`:${DealActivityType.QuestionAsked}:`);
}

export function isQuestionAnsweredGroup(group: ActivityGroup): boolean {
  return group.groupKey.includes(`:${DealActivityType.QuestionAnswered}:`);
}

export function isTaskCreatedGroup(group: ActivityGroup): boolean {
  return group.groupKey.endsWith(`:${DealActivityType.TaskCreated}`);
}
